import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { useAuth, canManageOps } from "@/lib/auth";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Plus, Trash2, ChevronRight, MapPin } from "lucide-react";

function useTree() {
  return useQuery({
    queryKey: ["facility-tree"],
    queryFn: async () => (await api.get("/facilities/tree/full")).data,
  });
}

export default function Facilities() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: tree } = useTree();
  const [expanded, setExpanded] = useState({});
  const manage = canManageOps(user);

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const [newFacility, setNewFacility] = useState({ name: "", address: "", facility_type: "Hotel", image_url: "" });
  const [creatingFacility, setCreatingFacility] = useState(false);
  const [openFacility, setOpenFacility] = useState(false);

  const submitFacility = async () => {
    if (!newFacility.name) return toast.error("Name required");
    setCreatingFacility(true);
    try {
      await api.post("/facilities", newFacility);
      toast.success("Facility created");
      setNewFacility({ name: "", address: "", facility_type: "Hotel", image_url: "" });
      setOpenFacility(false);
      qc.invalidateQueries({ queryKey: ["facility-tree"] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setCreatingFacility(false); }
  };

  const [addChild, setAddChild] = useState(null); // {kind, parentId}
  const [childName, setChildName] = useState("");
  const [childType, setChildType] = useState("Guest Room");
  const [childLevel, setChildLevel] = useState(1);

  const submitChild = async () => {
    if (!childName) return toast.error("Name required");
    try {
      if (addChild.kind === "building") {
        await api.post("/facilities/buildings", { facility_id: addChild.parentId, name: childName });
      } else if (addChild.kind === "floor") {
        await api.post("/facilities/floors", { building_id: addChild.parentId, name: childName, level: Number(childLevel) });
      } else if (addChild.kind === "area") {
        await api.post("/facilities/areas", { floor_id: addChild.parentId, name: childName, area_type: childType });
      }
      toast.success("Created");
      setAddChild(null); setChildName(""); setChildType("Guest Room"); setChildLevel(1);
      qc.invalidateQueries({ queryKey: ["facility-tree"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (kind, id) => {
    if (!window.confirm(`Delete this ${kind}? Child items will be removed.`)) return;
    try {
      await api.delete(kind === "facility" ? `/facilities/${id}` : `/facilities/${kind}s/${id}`);
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["facility-tree"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="label-caps text-muted-foreground mb-2">Configure</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Facilities</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-lg">
            Facility → Building → Floor → Area. Areas anchor every checklist and task.
          </p>
        </div>
        {manage && (
          <Dialog open={openFacility} onOpenChange={setOpenFacility}>
            <DialogTrigger asChild>
              <Button data-testid="add-facility-btn"><Plus className="w-4 h-4 mr-1" />New Facility</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create facility</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Name</Label>
                  <Input data-testid="facility-name" value={newFacility.name} onChange={(e) => setNewFacility({ ...newFacility, name: e.target.value })} placeholder="Hotel Cape Waterfront" /></div>
                <div><Label>Address</Label>
                  <Input value={newFacility.address} onChange={(e) => setNewFacility({ ...newFacility, address: e.target.value })} /></div>
                <div><Label>Type</Label>
                  <Select value={newFacility.facility_type} onValueChange={(v) => setNewFacility({ ...newFacility, facility_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Hotel","Lodge","Restaurant","Hospital","Guest House","Office"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Image URL (optional)</Label>
                  <Input value={newFacility.image_url} onChange={(e) => setNewFacility({ ...newFacility, image_url: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button onClick={submitFacility} disabled={creatingFacility} data-testid="facility-submit">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-4">
        {tree?.length === 0 && (
          <div className="border border-dashed border-border bg-card p-12 text-center">
            <Building2 className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <div className="font-medium">No facilities yet</div>
            <p className="text-sm text-muted-foreground mt-1">Create your first facility to get started.</p>
          </div>
        )}
        {tree?.map((f) => (
          <div key={f.id} className="border border-border bg-card rounded-2xl overflow-hidden shadow-sm" data-testid={`facility-${f.id}`}>
            <div className="flex items-stretch">
              {f.image_url && (
                <div className="w-40 h-32 shrink-0 hidden md:block relative">
                  <img src={f.image_url} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 p-5 flex items-start justify-between">
                <div className="min-w-0">
                  <div className="label-caps text-muted-foreground">{f.facility_type || "Facility"}</div>
                  <div className="font-display text-xl font-semibold mt-1">{f.name}</div>
                  {f.address && <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{f.address}</div>}
                  <div className="flex items-center gap-3 mt-3 text-xs">
                    <span className="px-2 py-1 bg-muted rounded-sm">{f.buildings?.length || 0} buildings</span>
                    <span className="px-2 py-1 bg-muted rounded-sm">
                      {f.buildings?.reduce((n, b) => n + (b.floors?.length || 0), 0)} floors
                    </span>
                    <span className="px-2 py-1 bg-muted rounded-sm">
                      {f.buildings?.reduce((n, b) => n + b.floors?.reduce((m, fl) => m + (fl.areas?.length || 0), 0), 0)} areas
                    </span>
                  </div>
                </div>
                {manage && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setAddChild({ kind: "building", parentId: f.id })} data-testid={`add-building-${f.id}`}>
                      <Plus className="w-3 h-3 mr-1" />Building
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del("facility", f.id)} data-testid={`delete-facility-${f.id}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Buildings */}
            <div className="border-t border-border">
              {f.buildings?.map((b) => (
                <div key={b.id} className="border-b border-border last:border-b-0">
                  <div className="flex items-center px-5 py-3 hover:bg-accent/30">
                    <button onClick={() => toggle(b.id)} className="flex items-center gap-2 flex-1 text-left" data-testid={`building-toggle-${b.id}`}>
                      <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${expanded[b.id] ? "rotate-90" : ""}`} />
                      <span className="text-sm font-medium">{b.name}</span>
                      <span className="text-xs text-muted-foreground">· {b.floors?.length || 0} floors</span>
                    </button>
                    {manage && (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setAddChild({ kind: "floor", parentId: b.id })}>
                          <Plus className="w-3 h-3 mr-1" />Floor
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => del("building", b.id)}>
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {expanded[b.id] && (
                    <div className="pl-9 pr-4 pb-3 space-y-1 bg-muted/20">
                      {b.floors?.map((fl) => (
                        <div key={fl.id} className="border-l-2 border-border pl-3 py-2">
                          <div className="flex items-center">
                            <div className="flex-1 text-sm font-medium">{fl.name}</div>
                            {manage && (
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setAddChild({ kind: "area", parentId: fl.id })}>
                                  <Plus className="w-3 h-3 mr-1" />Area
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => del("floor", fl.id)}>
                                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                </Button>
                              </div>
                            )}
                          </div>
                          {fl.areas?.length > 0 && (
                            <div className="mt-2 grid md:grid-cols-3 lg:grid-cols-4 gap-1">
                              {fl.areas.map((a) => (
                                <div key={a.id} className="px-3 py-2 bg-background border border-border text-xs flex items-center justify-between">
                                  <span className="truncate">{a.name} <span className="text-muted-foreground">· {a.area_type}</span></span>
                                  {manage && (
                                    <button onClick={() => del("area", a.id)} className="text-muted-foreground hover:text-destructive">
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!addChild} onOpenChange={(o) => !o && setAddChild(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add {addChild?.kind}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label>
              <Input value={childName} onChange={(e) => setChildName(e.target.value)} data-testid="child-name" /></div>
            {addChild?.kind === "floor" && (
              <div><Label>Level</Label>
                <Input type="number" value={childLevel} onChange={(e) => setChildLevel(e.target.value)} /></div>
            )}
            {addChild?.kind === "area" && (
              <div><Label>Type</Label>
                <Select value={childType} onValueChange={setChildType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Guest Room","Bathroom","Kitchen","Reception","Hallway","Laundry","Restaurant","Conference","Spa","Public"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={submitChild} data-testid="child-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
